import React, { useEffect, useMemo, useRef, useState } from "react";
import Adddish from "../components/DishForms/AddDish";
import { DishResponseI } from "../utils/types";
import { dishService, cuisineService, foodTypeService } from "../services";
import DishCard from "../components/DishCard";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const FILTER_CHIPS = [
  "100+ Sales",
  "Rated 4+",
  "Non-Veg",
  "Veg",
  "Biryani",
  "Pasta",
];

const dishMatchesFilter = (
  dish: DishResponseI,
  filter: string,
  lookups: {
    foodTypes: { _id: string; name: string }[];
    cuisines: { _id: string; name: string }[];
  }
): boolean => {
  const foodTypeName = (
    dish.foodType?.name ||
    lookups.foodTypes.find((item) => item._id === dish.foodTypeId)?.name ||
    ""
  ).toLowerCase();
  const cuisineName = (
    dish.cuisineType?.name ||
    lookups.cuisines.find((item) => item._id === dish.cuisineTypeId)?.name ||
    ""
  ).toLowerCase();
  const categoryName = dish.category?.name?.toLowerCase() || "";
  const dishName = dish.dishName?.toLowerCase() || "";
  const tags = (dish.tags || []).map((tag) => tag.name.toLowerCase());
  const searchBlob = `${dishName} ${cuisineName} ${categoryName} ${tags.join(" ")}`;

  switch (filter) {
    case "available":
      return dish.isAvailable;
    case "Veg":
      return foodTypeName.includes("veg") && !foodTypeName.includes("non");
    case "Non-Veg":
      return foodTypeName.includes("non");
    case "100+ Sales":
      return (dish.salesCount ?? dish.maxQuantityPerOrder ?? 0) >= 100;
    case "Rated 4+":
      return (dish.rating ?? 0) >= 4;
    default:
      return searchBlob.includes(filter.toLowerCase());
  }
};

const DishList = () => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDishId, setEditingDishId] = useState<string | undefined>(
    undefined
  );
  const [dishes, setDishes] = useState<DishResponseI[]>([]);
  const [loading, setLoading] = useState(true);
  const [foodTypes, setFoodTypes] = useState<{ _id: string; name: string }[]>(
    []
  );
  const [cuisines, setCuisines] = useState<{ _id: string; name: string }[]>([]);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  const fetchDishes = async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) setLoading(true);
      const response: any = await dishService.getDishes();
      setDishes(response.data || []);
    } catch (error: any) {
      console.error("Error fetching dishes:", error);
      toast.error(error.message || "Failed to fetch dishes");
    } finally {
      if (!options?.silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDishes();

    const fetchLookups = async () => {
      try {
        const [foodTypeRes, cuisineRes]: any[] = await Promise.all([
          foodTypeService.getFoodTypes(),
          cuisineService.getCuisines(),
        ]);
        if (foodTypeRes?.status && foodTypeRes.data) {
          setFoodTypes(foodTypeRes.data);
        }
        if (cuisineRes?.status && cuisineRes.data) {
          setCuisines(cuisineRes.data);
        }
      } catch (error) {
        console.error("Failed to load dish filter lookups:", error);
      }
    };

    fetchLookups();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target as Node)
      ) {
        setShowFilterOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredDishes = useMemo(() => {
    if (selectedFilters.length === 0) return dishes;
    return dishes.filter((dish) =>
      selectedFilters.every((filter) =>
        dishMatchesFilter(dish, filter, { foodTypes, cuisines })
      )
    );
  }, [dishes, selectedFilters, foodTypes, cuisines]);

  const handleToggleAvailability = async (id: string, available: boolean) => {
    const previousDishes = dishes;
    setDishes((prev) =>
      prev.map((dish) =>
        dish._id === id ? { ...dish, isAvailable: available } : dish
      )
    );

    try {
      const res: any = await dishService.toggleDishAvailability(id, available);
      toast.success(res.message);
    } catch (error: any) {
      setDishes(previousDishes);
      toast.error(error.message || "Failed to update dish availability");
    }
  };

  const handleEdit = (id: string) => {
    setEditingDishId(id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Dish",
      text: "Are you sure you want to delete this dish? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FF8A00",
      cancelButtonColor: "#6A6A6A",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    const previousDishes = dishes;
    // Soft-delete: API marks dish unavailable — update UI in place, don't remove card
    setDishes((prev) =>
      prev.map((dish) =>
        dish._id === id ? { ...dish, isAvailable: false } : dish
      )
    );

    try {
      const res: any = await dishService.deleteDish(id);
      toast.success(res.message || "Dish deleted successfully");
      await fetchDishes({ silent: true });
    } catch (error: any) {
      setDishes(previousDishes);
      console.error("Error deleting dish:", error);
      toast.error(error.message || "Failed to delete dish");
    }
  };

  const handleDynamicPricing = (id: string) => {
    console.log(`Dynamic pricing for dish ${id}`);
  };

  const handleAdddishes = () => {
    setEditingDishId(undefined);
    setShowAddForm(true);
  };

  const handleFormComplete = () => {
    setShowAddForm(false);
    setEditingDishId(undefined);
    fetchDishes();
  };

  const handleFormCancel = () => {
    setShowAddForm(false);
    setEditingDishId(undefined);
  };

  const toggleFilter = (filter: string) => {
    setSelectedFilters((prev) => {
      if (prev.includes(filter)) {
        return prev.filter((item) => item !== filter);
      }

      let next = [...prev, filter];
      if (filter === "Veg") next = next.filter((item) => item !== "Non-Veg");
      if (filter === "Non-Veg") next = next.filter((item) => item !== "Veg");
      return next;
    });
  };

  const handleAllDishes = () => {
    setSelectedFilters([]);
    setShowFilterOptions(false);
    fetchDishes();
  };

  const handleAvailableOnly = () => {
    setSelectedFilters(["available"]);
    setShowFilterOptions(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] dark:bg-gray-900 transition-colors">
      {showAddForm ? (
        <Adddish
          dishId={editingDishId}
          onComplete={handleFormComplete}
          onCancel={handleFormCancel}
        />
      ) : (
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-white dark:bg-gray-800 md:rounded-lg px-5 py-4 shadow-sm border-b dark:border-gray-700 transition-colors">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full md:w-auto">
                <div className="flex items-center justify-between w-full md:w-auto">
                  <h1 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[20px] md:text-[24px] font-semibold">
                    Listed Dishes
                  </h1>
                  <button
                    onClick={handleAdddishes}
                    className="md:hidden bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-[14px] font-semibold px-4 py-2 rounded-lg transition-colors"
                  >
                    + Add Dish
                  </button>
                </div>

                <div className="flex items-center gap-2 md:gap-3 overflow-x-auto scrollbar-hide w-full md:w-auto">
                  <div className="relative flex-shrink-0" ref={filterMenuRef}>
                    <button
                      type="button"
                      onClick={() => setShowFilterOptions((prev) => !prev)}
                      className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-lg font-Urbanist text-[12px] md:text-[14px] font-normal transition-colors whitespace-nowrap ${
                        selectedFilters.includes("available") ||
                        showFilterOptions
                          ? "bg-[#FF8A00] text-white"
                          : "bg-[#F5F5F5] dark:bg-gray-700 text-[#050505] dark:text-gray-200 hover:bg-[#EEEEEE] dark:hover:bg-gray-600"
                      }`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="md:w-4 md:h-4"
                      >
                        <path
                          d="M2 4H14M4 8H12M6 12H10"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      Filters
                      <svg
                        width="8"
                        height="5"
                        viewBox="0 0 10 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="md:w-2.5 md:h-1.5"
                      >
                        <path
                          d="M1 1L5 5L9 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {showFilterOptions && (
                      <div className="absolute left-0 top-full mt-2 z-20 min-w-[180px] rounded-lg border border-[#E9E9E9] dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={handleAllDishes}
                          className="w-full text-left px-4 py-2.5 font-Urbanist text-[14px] text-[#050505] dark:text-gray-200 hover:bg-[#F5F5F5] dark:hover:bg-gray-700"
                        >
                          All Dishes
                        </button>
                        <button
                          type="button"
                          onClick={handleAvailableOnly}
                          className="w-full text-left px-4 py-2.5 font-Urbanist text-[14px] text-[#050505] dark:text-gray-200 hover:bg-[#F5F5F5] dark:hover:bg-gray-700"
                        >
                          Available Only
                        </button>
                      </div>
                    )}
                  </div>

                  {FILTER_CHIPS.map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => toggleFilter(filter)}
                      className={`px-3 md:px-4 py-2 rounded-lg text-[12px] md:text-[14px] font-Urbanist font-normal transition-colors whitespace-nowrap flex-shrink-0 ${
                        selectedFilters.includes(filter)
                          ? "bg-[#FF8A00] text-white"
                          : "bg-[#F5F5F5] dark:bg-gray-700 text-[#050505] dark:text-gray-200 hover:bg-[#EEEEEE] dark:hover:bg-gray-600"
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleAdddishes}
                className="hidden md:block bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-[14px] font-semibold px-6 py-2.5 rounded-lg transition-colors"
              >
                Add Dish
              </button>
            </div>
          </div>

          <div className="bg-[#F9F9F9] dark:bg-gray-800 md:bg-[#FAFAFA] md:rounded-lg py-3 md:py-5 w-full mx-auto px-4 md:px-0 pb-24 md:pb-5">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF8A00]" />
              </div>
            ) : filteredDishes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[16px] font-medium">
                  No dishes found. Add your first dish!
                </p>
              </div>
            ) : (
              filteredDishes.map((dish) => (
                <DishCard
                  key={dish._id}
                  id={dish._id}
                  image={
                    dish.images?.find((img) => img.isPrimary)?.url ||
                    dish.images?.[0]?.url
                  }
                  name={dish.dishName}
                  rating={dish.rating}
                  review={dish.reviewCount}
                  price={dish.basePrice}
                  salesCount={dish.salesCount ?? dish.maxQuantityPerOrder}
                  isAvailable={dish.isAvailable}
                  onToggleAvailability={handleToggleAvailability}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onDynamicPricing={handleDynamicPricing}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DishList;
